import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Account, ChartPoint, ChartRange, User } from '@wml-insta/shared';
import { ACTIVE_STATUSES, isArchivedStatus } from '@wml-insta/shared';
import { CHART_RANGES } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { useAccountsData } from '../lib/useAccountsData.js';
import { loadPersisted, savePersisted } from '../lib/persist.js';
import { firstLetter, formatNum } from '../lib/format.js';
import { UpdatedNote } from '../ui/UpdatedNote.js';
import { AccountsTable } from '../ui/AccountsTable.js';
import { AccountFilters, EMPTY_FILTERS, filterAccounts, type AccountFiltersValue } from '../ui/AccountFilters.js';
import { ImportAccountsModal } from '../ui/ImportAccountsModal.js';
import { NameModal } from '../ui/NameModal.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { SnapshotChart } from '../charts/SnapshotChart.js';
import { ModalPortal } from '../ui/ModalPortal.js';

function validRange(v: string, fallback: ChartRange = '7d'): ChartRange {
  return (CHART_RANGES as string[]).includes(v) ? (v as ChartRange) : fallback;
}

const RANGES: { key: ChartRange; label: string }[] = [
  { key: '7d', label: '7 дней' },
  { key: '1m', label: 'Месяц' },
  { key: '3m', label: '3 месяца' },
  { key: 'all', label: 'Всё время' },
];

export function ModelPage({ me }: { me: User }) {
  const { id } = useParams();
  const nav = useNavigate();
  const data = useAccountsData();
  // Фильтры сохраняются в localStorage — переживают перезагрузку и переходы.
  const [filters, setFiltersRaw] = useState<AccountFiltersValue>(
    () => ({ ...EMPTY_FILTERS, ...loadPersisted(me.id, 'filters.model', {}) }),
  );
  const setFilters = (next: AccountFiltersValue) => {
    setFiltersRaw(next);
    savePersisted(me.id, 'filters.model', next);
  };
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [merging, setMerging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [chart, setChart] = useState<ChartPoint[]>([]);
  // График: открыт/скрыт и последний таймфрейм — запоминаются на юзера.
  const [range, setRangeRaw] = useState<ChartRange>(() => validRange(loadPersisted<string>(me.id, 'chart.model.range', '7d')));
  const setRange = (r: ChartRange) => { setRangeRaw(r); savePersisted(me.id, 'chart.model.range', r); };
  const [showChart, setShowChartRaw] = useState<boolean>(() => loadPersisted<boolean>(me.id, 'chart.model.open', false));
  const setShowChart = (updater: (v: boolean) => boolean) => {
    setShowChartRaw((v) => {
      const next = updater(v);
      savePersisted(me.id, 'chart.model.open', next);
      return next;
    });
  };

  const model = data.models.find((m) => m.id === id) ?? null;
  const modelAccounts = useMemo(
    () => data.accounts.filter((a) => a.modelId === id),
    [data.accounts, id],
  );
  // Архивные — в «Архиве». Воронка и график ниже считаются по modelAccounts
  // (всем аккам модели), чтобы просмотры банов/выключенных не пропадали.
  const workingAccounts = useMemo(
    () => modelAccounts.filter((a) => !isArchivedStatus(a.status) || data.archiveGrace.has(a.id)),
    [modelAccounts, data.archiveGrace],
  );
  const filtered = useMemo(
    () => filterAccounts(workingAccounts, filters),
    [workingAccounts, filters],
  );

  // График подстраивается под выбранную пачку акков (фильтр по датам старта).
  const chartStartDates = filters.startDates.join(',');
  useEffect(() => {
    if (!id || !showChart) return;
    const extra = chartStartDates ? `&startDates=${encodeURIComponent(chartStartDates)}` : '';
    api<ChartPoint[]>(`/api/stats/chart?range=${range}&modelId=${id}${extra}`).then(setChart).catch(() => setChart([]));
  }, [id, range, showChart, chartStartDates]);

  if (!data.loaded) return <div className="empty-state">Загрузка…</div>;
  if (!model) return <div className="empty-state">Модель не найдена.</div>;

  // Воронка этапов — из серверных агрегатов модели: они считаются по ВСЕМ
  // аккам (включая архивные), а страница грузит только рабочие.
  const st = (key: string) => model.byStatus[key] ?? 0;
  const funnel = [
    { label: 'Прогрев', value: st('Прогрев'), color: '#eab308' },
    { label: 'Оформлено', value: model.decoratedCount, color: undefined },
    { label: 'Залито', value: model.pouredCount, color: undefined },
    { label: 'В работе', value: st('В работе'), color: '#4ade80' },
    { label: 'Хьюман', value: st('Хьюман'), color: '#60a5fa' },
    { label: 'Бан', value: st('Бан'), color: 'var(--accent)' },
    { label: 'Выключение', value: st('Выключение'), color: undefined },
  ];

  return (
    <div className="page-inner page-fill">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div className="head-avatar" style={{ width: 44, height: 44, fontSize: 19, borderRadius: 12, border: '1px solid var(--accent)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 24px var(--accent-softer)' }}>
            {firstLetter(model.name)}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>{model.name}</h1>
              <button className="rename-btn" onClick={() => setRenaming(true)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                </svg>
                Переименовать
              </button>
              <button className="rename-btn" onClick={() => setMerging(true)} title="Слить эту модель в другую (дубли имён)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M8 7h12M8 12h12M8 17h12" />
                  <path d="m3 7 2 2 2-2" transform="translate(-1,3)" />
                </svg>
                Объединить
              </button>
              {me.role === 'admin' && (
                <button className="link-btn danger" onClick={() => setConfirmDelete(true)}>удалить</button>
              )}
            </div>
            <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span>
                {model.accountCount} акк. на {new Set(modelAccounts.map((a) => a.phoneId)).size} телефонах ·{' '}
                {formatNum(model.views24h)} просм./24ч · {formatNum(model.viewsTotal)} всего
              </span>
              <UpdatedNote at={model.lastParsedAt} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={() => setShowChart((v) => !v)}>
            {showChart ? 'Скрыть график' : 'График'}
          </button>
          <button className="btn-secondary" onClick={() => setImporting(true)}>Импорт из файла</button>
          <button className="btn-primary btn-add-account" onClick={() => setAdding((v) => !v)}>
            {adding ? '✕ Отмена' : '+ Добавить аккаунт'}
          </button>
        </div>
      </div>

      <div className="funnel-row">
        {funnel.map((f) => (
          <div key={f.label} className="funnel-step">
            <div className="fs-label">{f.label}</div>
            <div className="fs-value" style={f.color ? { color: f.color } : undefined}>{f.value}</div>
          </div>
        ))}
      </div>

      {showChart && (
        <div className="card card-large" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <div className="range-tabs range-tabs-compact">
              {RANGES.map((r) => (
                <button key={r.key} className={range === r.key ? 'active' : ''} onClick={() => setRange(r.key)}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <SnapshotChart points={chart} height={280} />
        </div>
      )}

      {data.error && <div className="error" style={{ marginBottom: 12 }}>{data.error}</div>}

      <AccountsTable
        accounts={filtered}
        me={me}
        phones={data.phones}
        models={data.models}
        tableKey="model"
          rowActions={filters.unworked ? (a) => (
            a.unworked ? (
              <button
                className="row-open-btn"
                title="Видел: скрыть из необработавших до следующего апдейта отработок"
                style={{ color: 'var(--accent)' }}
                onClick={async () => {
                  const updated = await api<Account>(`/api/accounts/${a.id}/workout-dismiss`, { method: 'POST' });
                  data.applyPatched(updated);
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            ) : null
          ) : undefined}
        hiddenByDefault={['model']}
        defaultModelId={model.id}
        draftOpen={adding}
        onDraftClose={() => setAdding(false)}
        onPatched={data.applyPatched}
        onDeleted={data.applyDeleted}
        onCreated={(a) => { data.applyCreated(a); }}
        onOpen={(a) => nav(`/account/${a.id}`)}
        onError={data.setError}
        toolbar={
          <AccountFilters
            value={filters}
            onChange={setFilters}
            accounts={workingAccounts}
            phones={data.phones}
            showModel={false}
            statusOptions={ACTIVE_STATUSES}
          />
        }
      />

      {importing && (
        <ImportAccountsModal
          phones={data.phones}
          models={data.models}
          defaultModelId={model.id}
          onClose={() => setImporting(false)}
          onDone={() => data.reload()}
        />
      )}

      {renaming && (
        <NameModal
          title="Переименовать модель"
          label="Имя модели"
          initial={model.name}
          onClose={() => setRenaming(false)}
          onSubmit={async (name) => {
            await api(`/api/models/${model.id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
            data.reload();
          }}
        />
      )}

      {merging && (
        <MergeModelModal
          fromModel={model}
          models={data.models}
          onClose={() => setMerging(false)}
          onDone={(toId) => nav(`/model/${toId}`)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Удалить модель?"
          message={<>Модель <b>{model.name}</b> будет удалена. Её акки останутся, но без модели.</>}
          confirmLabel="Удалить"
          danger
          onConfirm={async () => {
            await api(`/api/models/${model.id}`, { method: 'DELETE' });
            nav('/models');
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

function MergeModelModal({
  fromModel, models, onClose, onDone,
}: {
  fromModel: { id: string; name: string; accountCount: number };
  models: { id: string; name: string; accountCount: number }[];
  onClose: () => void;
  onDone: (toId: string) => void;
}) {
  const others = models.filter((m) => m.id !== fromModel.id);
  const [toId, setToId] = useState(others[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!toId) return;
    setBusy(true);
    setErr(null);
    try {
      await api('/api/models/merge', { method: 'POST', body: JSON.stringify({ fromId: fromModel.id, toId }) });
      onDone(toId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не получилось');
      setBusy(false);
    }
  }

  return (
    <ModalPortal>
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Объединить модели</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
          Все {fromModel.accountCount} акк. модели <b>{fromModel.name}</b> переедут в выбранную модель,
          а «{fromModel.name}» будет удалена. Используй для дублей типа «КьютиЕва» = «CutieEva».
        </div>
        <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          <label className="field">
            <span>Влить в модель</span>
            <select className="admin-input" value={toId} onChange={(e) => setToId(e.target.value)}>
              {others.map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({m.accountCount} акк.)</option>
              ))}
            </select>
          </label>
          {err && <div className="error">{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={busy || !toId} style={{ width: 'auto', padding: '0 20px' }}>
              {busy ? 'Объединяю…' : 'Объединить'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
}
